import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, Button, ProgressBar, Text } from "components";
import { selectGameId, selectGameObject } from "features/gamestate/gameStateSlice";
import { selectQuestionEntities } from "features/question/questionSlice";
import { selectAllPlayers } from "features/player/playerSlice";
import { computePlayProgress } from "../lifecycle";
import { liveStateLabel, controllingPlayerName } from "./liveState";
import { openBoardInNewTab } from "./launchFlow";
import "./LiveNowCard.css";

/**
 * Game Home "Live now" card — shown only while the game is in progress. A lean
 * cockpit glimpse built entirely from already-watched state (no extra reads):
 * the current phase/round, the active clue, clues played, and player count.
 */
const LiveNowCard = () => {
  const game = useSelector(selectGameObject);
  const gameId = useSelector(selectGameId);
  const questions = useSelector(selectQuestionEntities);
  const players = useSelector(selectAllPlayers);
  const navigate = useNavigate();

  if (!game) return null;

  const label = liveStateLabel(game, {
    selectingPlayer: controllingPlayerName(game, players),
  });
  const { played, total, percentage } = computePlayProgress(game);
  const qid = game.state?.questionId;
  const q = qid ? questions?.[qid] : null;
  const clue = q ? `${q.category || "—"}${q.value ? ` · $${q.value}` : ""}` : null;
  const count = players.length;

  return (
    <Card accent="teal" padding="lg" className="gx-live">
      <div className="gx-live-main">
        <div className="gx-live-eyebrow">
          <span className="gx-live-dot" aria-hidden="true" />
          <Text variant="eyebrow" tone="accent">Live now</Text>
        </div>
        <Text variant="h2" className="gx-live-state">{label}</Text>
        {clue && (
          <Text variant="body" tone="muted" className="gx-live-clue">
            Current clue · {clue}
          </Text>
        )}
        <div className="gx-live-meta">
          <span>{played}/{total} clues played</span>
          <span className="gx-live-sep" aria-hidden="true">·</span>
          <span>{count} {count === 1 ? "player" : "players"}</span>
        </div>
        <ProgressBar value={percentage} tone="accent" height={6} />
      </div>
      <div className="gx-live-cta">
        <Button
          variant="primary"
          size="md"
          onClick={() => navigate(`/manage/game/${gameId}/control-room`)}
        >
          Open Control Room
        </Button>
        <Button variant="secondary" size="md" onClick={() => openBoardInNewTab(gameId)}>
          Open Board
        </Button>
      </div>
    </Card>
  );
};

export default LiveNowCard;
